import React, {
  useState,
  useEffect,
  Fragment,
  useCallback,
  useRef,
} from 'react';
import {
  Link,
  useSearchParams,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { Spin } from 'antd';
import {
  Input,
  Radio,
  TextArea,
  Dialog,
  Popup,
  PickerView,
  Toast,
} from 'antd-mobile';
import { CalendarOutline } from 'antd-mobile-icons';
import { CaretDownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CalendarPickerView } from 'antd-mobile';
import toyotaLogo from '../../assets/TOYOTA LOGO - Horizontal.svg';
import carServiceBookingSvg from '../../assets/car-service-booking.svg';
import useLineLiff from '../../hooks/LineLiff';
import styles from './BindingPage.module.scss';
import successSvg from '../../assets/success.svg';
import failureSvg from '../../assets/status-fail.svg';

import {
  fetchServiceFactory,
  fetchTwinFactory,
  fetchAvailableTimeV2,
  submitBooking,
  submitMaintenanceBooking,
  modifyBooking,
  fetchPastBooking,
  fetchMaintainanceAdvice,
  fetchBookingAvailable,
  StringFormatter,
} from './CarServiceBookingAction';

import './common.scss';
import './CarServiceBooking.scss';

function CustomCheckbox({ checked, onChange, disabled }) {
  return (
    <div
      className={`csb-checkbox${checked ? ' csb-checkbox--checked' : ''}${
        disabled ? ' csb-checkbox--disabled' : ''
      }`}
      onClick={() => !disabled && onChange(!checked)}
    />
  );
}

const contactTimeOptions = [
  { label: '08:00~12:00', value: '08:00' },
  { label: '13:00~17:30', value: '13:00' },
  { label: '18:00~19:30', value: '18:00' },
  { label: '任何時段（08:00~19:30）', value: 'anytime' },
  { label: '無需電話聯絡', value: 'none' },
];

const notifyOptions = [
  // { label: 'LINE', value: 'LINE' },
  // { label: 'My Toyota App', value: 'App' },
  { label: '簡訊', value: 'sms' },
  // { label: '電話', value: 'phone' },
  // { label: '無需提醒', value: 'none' },
];

const isServiceFactory = (locationName) => {
  return !locationName.includes('保修站');
};

// 手機號碼格式：需為 09 開頭，總共 10 碼數字
const PHONE_REGEX = /^09\d{8}$/;

// 將字面上的轉義字元（例如從後端傳來的 \n、\r\n、\t 等）還原成真正的特殊字元，
// 讓 textarea 能正確顯示換行、製表符等
const unescapeCommonChars = (str) =>
  str.replace(/\\r\\n|\\(n|r|t|"|'|\\)/g, (match, ch) => {
    if (match === '\\r\\n') return '\n';
    switch (ch) {
      case 'n':
      case 'r':
        return '\n';
      case 't':
        return '\t';
      case '"':
        return '"';
      case "'":
        return "'";
      case '\\':
        return '\\';
      default:
        return match;
    }
  });

// content 參數後端會以 base64 編碼傳遞（避免中文字直接放在 query string 上被 WAF 擋下），
// 前端需先解碼回原本的 UTF-8 文字
const decodeBase64Content = (base64Str) => {
  try {
    const binary = atob(base64Str);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  } catch (error) {
    console.error('base64 decode content failed', error);
    return base64Str;
  }
};

// 更改預約時，用來從 repairs 純文字反推服務項目勾選狀態的已知關鍵字
const KNOWN_CHECKBOX_ITEMS = ['導航圖資升級', '異音、抖動'];

export default function CarServiceBooking() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { liff, userInfo } = useLineLiff(process.env.REACT_APP_LIFF_BOOKING);
  const availabilityChecked = useRef(false);
  const liffState = searchParams.get('liff.state');
  const carNo = searchParams.get('carNo');
  const sKey = searchParams.get('sKey');
  const content = searchParams.get('content');
  const prefillRecord = location.state?.prefillRecord || null;
  const isEditMode = !!prefillRecord;

  // 確保頁面加載時滾動到頂部
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const [serviceItems, setServiceItems] = useState({ 定期保養: false });
  const [otherRepairs, setOtherRepairs] = useState({
    導航圖資升級: false,
    '異音、抖動': false,
    自行輸入: false,
  });
  const [customText, setCustomText] = useState('');

  const [form, setForm] = useState({
    plate: '',
    name: '',
    phone: '',
    serviceType: '',
    city: '',
    district: '',
    center: '',
    date: '',
    time: '',
    wait: '',
    contactTime: 'none',
    notify: 'none',
    comment: '',
  });
  const [errors, setErrors] = useState({});
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [contactTimeVisible, setContactTimeVisible] = useState(false);
  const [cityVisible, setCityVisible] = useState(false);
  const [districtVisible, setDistrictVisible] = useState(false);
  const [centerVisible, setCenterVisible] = useState(false);
  const [notifyVisible, setNotifyVisible] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [completeDialog, setCompleteDialog] = useState({
    visible: false,
    success: false,
    message: '',
    detail: '',
    autoClose: false,
  });
  const [errorDialog, setErrorDialog] = useState('');
  const [tempContactTime, setTempContactTime] = useState('');
  const [tempCity, setTempCity] = useState('');
  const [tempDistrict, setTempDistrict] = useState('');
  const [tempCenter, setTempCenter] = useState('');
  const [tempNotify, setTempNotify] = useState('');
  const [tempDate, setTempDate] = useState('');
  const [pendingOrder, setPendingOrder] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);

  // 依 serviceItems / otherRepairs / customText 計算 rm_flag、fix_flag，
  // 供「查詢可預約時段」與「送出預約/更改預約」共用，確保兩邊邏輯一致
  const computeRmFixFlags = useCallback(() => {
    const rmFlag = serviceItems['定期保養'] ? 'Y' : 'N';
    const hasFixFlag =
      Object.entries(otherRepairs).some(
        ([key, checked]) => checked && key !== '自行輸入'
      ) ||
      (otherRepairs['自行輸入'] && customText);
    return { rmFlag, fixFlag: hasFixFlag ? 'Y' : 'N' };
  }, [serviceItems, otherRepairs, customText]);

  // 「定期保養」與「其他維修需求」至少要擇一，供選擇服務廠與確定送出時共用檢查
  const hasServiceSelection = useCallback(() => {
    const { rmFlag, fixFlag } = computeRmFixFlags();
    return rmFlag === 'Y' || fixFlag === 'Y';
  }, [computeRmFixFlags]);

  //[Available Factory]
  const [fetchingFactory, setFetchingFactory] = useState(false);
  const [serviceFactory, setServiceFactory] = useState(null);
  const [lastBooking, setLastBooking] = useState();
  const [lastAdvice, setLastAdvice] = useState();
  const [lastAdviceFactory, setLastAdviceFactory] = useState();
  const [twinFactory, setTwinFactory] = useState();
  useEffect(() => {
    if (liffState || !liff || !userInfo) return;
    if (liffState || !carNo) return;

    const idToken = liff.getIDToken();
    const fetchingLastBooking = async () => {
      if (!sKey) return;

      try {
        const lastBookingResponse = await fetchPastBooking(
          idToken,
          carNo,
          sKey
        );
        if (lastBookingResponse.success) {
          if (lastBookingResponse.data === null) {
            throw new Error('無法取得上次預約資料');
          }
          setLastBooking(lastBookingResponse.data);
          if (!prefillRecord) {
            setForm((prev) => ({
              ...prev,
              phone: lastBookingResponse.data?.contact_phone || '',
            }));
          }
        } else {
          throw new Error(lastBookingResponse.message);
        }
      } catch (error) {
        console.error(error);
        setCompleteDialog({
          visible: true,
          success: false,
          message: error.message || '無法取得上次預約資料',
          autoClose: true,
        });
      }
    };
    const fetchingServiceFactory = async () => {
      setFetchingFactory(true);
      try {
        const factoryResponse = await fetchServiceFactory(idToken, carNo);
        if (factoryResponse.success) {
          setServiceFactory(factoryResponse.data);
        } else {
          throw new Error(factoryResponse.message);
        }
      } catch (error) {
        console.error(error);
        Toast.show({
          content: error.message || '取得服務廠資料失敗',
          icon: 'fail',
        });
      } finally {
        setFetchingFactory(false);
      }
    };
    const fetchingMaintainanceAdvice = async () => {
      try {
        const adviceResponse = await fetchMaintainanceAdvice(idToken, carNo);
        if (adviceResponse.success) {
          setLastAdvice(adviceResponse.data);
        } else {
          console.error(adviceResponse.message);
          // throw new Error(adviceResponse.message);
          throw new Error('無法取得上次回廠資訊');
        }
      } catch (error) {
        console.error(error);
        Toast.show({
          content: error.message || '取得定保紀錄失敗',
          icon: 'fail',
        });
      }
    };

    setForm((prev) => ({ ...prev, plate: carNo }));

    if (!isEditMode) {
      const checkAvailability = async () => {
        if (availabilityChecked.current) return;
        availabilityChecked.current = true;
        const checkingToast = Toast.show({
          content: '確認預約資格中...',
          icon: 'loading',
          duration: 0,
        });
        try {
          const availableResponse = await fetchBookingAvailable(
            liff.getIDToken(),
            carNo
          );
          checkingToast.close();
          if (
            !availableResponse.success ||
            availableResponse.data?.book_23 !== 'Y'
          ) {
            await Dialog.alert({
              content: '您已有相關預約，請至預約紀錄確認相關資訊',
              confirmText: '確定',
            });
            const params = new URLSearchParams();
            params.set('carNo', carNo);
            if (sKey) params.set('sKey', sKey);
            navigate(
              `/hotai/toyota/car-service-booking/record?${params.toString()}`
            );
            return;
          }
        } catch (error) {
          checkingToast.close();
          console.error(error);
        }
      };
      checkAvailability();
    }

    fetchingServiceFactory();
    if (sKey) {
      fetchingLastBooking();
    } else if (!prefillRecord) {
      fetchingMaintainanceAdvice();
    }
  }, [liffState, carNo, sKey, liff, userInfo]);

  const findFactoryFromList = useCallback(
    (dlrCode, brnhCode, callback) => {
      if (!serviceFactory) return;

      // Yes, It's O(n^3), Whatever.
      let stop = false;
      for (const dataLocation of Object.values(serviceFactory)) {
        if (stop) break;
        for (const dataZoom of Object.values(dataLocation)) {
          if (stop) break;
          for (const data of dataZoom) {
            if (stop) break;
            if (data.brnh_code === brnhCode && data.dlr_code === dlrCode) {
              callback(data);
              stop = true;
            }
          }
        }
      }
    },
    [serviceFactory]
  );

  useEffect(() => {
    if (!lastAdvice || !serviceFactory) return;

    findFactoryFromList(lastAdvice.dlr_code, lastAdvice.brnh_code, (data) => {
      setLastAdviceFactory(data);
    });
  }, [findFactoryFromList, lastAdvice, serviceFactory]);

  useEffect(() => {
    if (!serviceFactory) return;
    if (!lastAdvice && !lastBooking) return;

    let dlrCode = '';
    let brnhCode = '';
    if (lastAdvice) {
      dlrCode = lastAdvice.dlr_code;
      brnhCode = lastAdvice.brnh_code;
    } else if (lastBooking) {
      dlrCode = lastBooking.dlr_code;
      brnhCode = lastBooking.brnh_code;
    } else {
      return;
    }

    if (dlrCode.trim() === '' || brnhCode.trim() === '') {
      return;
    }

    const idToken = liff.getIDToken();
    const fetchingTwinFactory = async () => {
      try {
        const twinFactoryResponse = await fetchTwinFactory(
          idToken,
          carNo,
          dlrCode,
          brnhCode
        );
        if (twinFactoryResponse.success) {
          if (
            twinFactoryResponse.data?.dlr_code &&
            twinFactoryResponse.data?.brnh_code
          ) {
            findFactoryFromList(
              twinFactoryResponse.data.dlr_code,
              twinFactoryResponse.data.brnh_code,
              (data) => {
                setTwinFactory(data);
              }
            );
          }
          console.log('查無子保修站選項');
        } else {
          throw new Error(twinFactoryResponse.message);
        }
      } catch (error) {
        console.error(error);
        Toast.show({
          content: error.message || '取得子保修站資料失敗',
          icon: 'fail',
        });
      }
    };

    fetchingTwinFactory();
  }, [
    carNo,
    findFactoryFromList,
    lastAdvice,
    lastBooking,
    liff,
    serviceFactory,
  ]);

  useEffect(() => {
    if (!prefillRecord) return;

    setForm((f) => ({
      ...f,
      name: prefillRecord.contactName || f.name,
      phone: prefillRecord.contactTel || f.phone,
      wait:
        prefillRecord.wait === '是'
          ? 'yes'
          : prefillRecord.wait === '否'
          ? 'no'
          : f.wait,
    }));

    const repairs = prefillRecord.repairs || '';
    if (repairs) {
      // 從既有的 repairs 純文字反推各項目的勾選狀態，
      // 讓後續計算 rm_flag / fix_flag 時能正確反映原本的預約內容
      const blocks = repairs
        .split(/[;；]/)
        .map((b) => b.trim())
        .filter(Boolean);
      const lastBlock = blocks[blocks.length - 1] || '';
      const cleanLastBlock = KNOWN_CHECKBOX_ITEMS.reduce(
        (str, item) => str.replace(item, ''),
        lastBlock.replace('定期保養', '')
      )
        .replace(/[；、]/g, '')
        .trim();

      setServiceItems((s) => ({
        ...s,
        定期保養: repairs.includes('定期保養'),
      }));
      setOtherRepairs((s) => ({
        ...s,
        導航圖資升級: repairs.includes('導航圖資升級'),
        '異音、抖動': repairs.includes('異音、抖動'),
        自行輸入: cleanLastBlock.length > 0,
      }));
      setCustomText(cleanLastBlock);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillRecord]);

  // 非更改預約模式下，若網址帶有 content 參數，將其內容帶入「自行輸入」
  // 欄位並自動勾選，讓使用者從特定通知進來時能直接看到預填的維修需求
  useEffect(() => {
    if (prefillRecord || !content) return;
    // content 是後端 base64 編碼過的中文字，先解碼還原成原本文字，
    // 若內含字面上的轉義字元（如 \n、\r\n、\t 等）再一併還原成真正的特殊字元，
    // textarea 才能正確顯示換行、製表符等
    const normalizedContent = unescapeCommonChars(decodeBase64Content(content));
    setOtherRepairs((s) => ({ ...s, 自行輸入: true }));
    setCustomText(normalizedContent);
  }, [prefillRecord, content]);

  useEffect(() => {
    if (!prefillRecord || !serviceFactory) return;
    if (!prefillRecord.dlrCode || !prefillRecord.brnhCode) return;

    let foundFactory = null;
    outer: for (const [, districts] of Object.entries(serviceFactory)) {
      for (const [, factories] of Object.entries(districts)) {
        for (const factory of factories) {
          if (
            factory.brnh_code === prefillRecord.brnhCode &&
            factory.dlr_code === prefillRecord.dlrCode
          ) {
            foundFactory = factory;
            break outer;
          }
        }
      }
    }

    if (foundFactory) {
      setLastBooking(foundFactory);
      setSelectedCenter(foundFactory);
      setForm((f) => ({ ...f, serviceType: 'last' }));

      const fetchPrefillTwinFactory = async () => {
        try {
          const idToken = liff.getIDToken();
          const twinRes = await fetchTwinFactory(
            idToken,
            carNo,
            prefillRecord.dlrCode,
            prefillRecord.brnhCode
          );
          if (
            twinRes.success &&
            twinRes.data?.dlr_code &&
            twinRes.data?.brnh_code
          ) {
            findFactoryFromList(
              twinRes.data.dlr_code,
              twinRes.data.brnh_code,
              (data) => setTwinFactory(data)
            );
          }
        } catch (error) {
          console.error(error);
        }
      };

      fetchPrefillTwinFactory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillRecord, serviceFactory]);

  //[Available Time]
  const [fetchingTime, setFetchingTime] = useState(false);
  const [availableTime, setAvailableTime] = useState({});
  useEffect(() => {
    if (!selectedCenter) return;

    const { rmFlag, fixFlag } = computeRmFixFlags();

    const fetchingAvailableTime = async () => {
      setFetchingTime(true);
      setAvailableTime({});
      try {
        const timeResponse = await fetchAvailableTimeV2(
          liff.getIDToken(),
          carNo,
          selectedCenter.dlr_code,
          selectedCenter.brnh_code,
          rmFlag,
          fixFlag
        );
        if (timeResponse.success) {
          const rawList = timeResponse.data?.time_list || [];
          const transformed = rawList.reduce((acc, item) => {
            const date = (item.date || '').replace(/\//g, '-');
            if (!date) return acc;
            if (!acc[date]) acc[date] = [];
            if (item.time) acc[date].push(item.time);
            return acc;
          }, {});
          if (Object.keys(transformed).length > 0) {
            setAvailableTime(transformed);
          } else {
            Toast.show({
              content: '該服務廠暫無可預約時段',
              icon: 'fail',
            });
          }
          return;
        } else {
          throw new Error(timeResponse.message);
        }
      } catch (error) {
        console.error(error);
        Toast.show({
          content: error.message || '取得可預約時段失敗',
          icon: 'fail',
        });
      } finally {
        setFetchingTime(false);
      }
    };

    fetchingAvailableTime();
  }, [selectedCenter, liff, serviceItems, otherRepairs, customText, carNo]);

  const handleSubmit = () => {
    const newErrors = {};
    if (!form.plate) newErrors.plate = '必填';
    if (!form.name) newErrors.name = '必填';
    if (!form.phone) newErrors.phone = '必填';
    else if (!PHONE_REGEX.test(form.phone))
      newErrors.phone = '手機號碼格式錯誤';
    if (!isEditMode && !hasServiceSelection()) {
      newErrors.serviceSelection =
        '請至少選擇「定期保養」或「其他維修需求」其中一項';
    }
    if (!form.serviceType) newErrors.serviceType = '必填';
    if (form.serviceType === 'custom') {
      if (!form.city) newErrors.city = '必填';
      if (!form.district) newErrors.district = '必填';
      if (!form.center) newErrors.center = '必填';
    }
    if (!form.date) newErrors.date = '必填';
    if (!form.time) newErrors.time = '必填';
    if (!form.wait) newErrors.wait = '必填';
    // if (!form.contactTime) newErrors.contactTime = '必填';
    if (!form.notify) newErrors.notify = '必填';
    console.log(`newErrors`, newErrors);
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      // 組合服務項目和其他維修需求為 comment
      const selectedServices = Object.entries(serviceItems)
        .filter(([, checked]) => checked)
        .map(([name]) => name);
      const selectedRepairs = Object.entries(otherRepairs)
        .filter(([key, checked]) => checked && key !== '自行輸入')
        .map(([name]) => name);
      const customInput =
        otherRepairs['自行輸入'] && customText ? customText : '';

      const commentParts = [];
      if (selectedServices.length > 0) {
        commentParts.push(`${selectedServices.join('、')}`);
      }
      if (selectedRepairs.length > 0) {
        commentParts.push(`${selectedRepairs.join('、')}`);
      }
      if (customInput) {
        commentParts.push(`${customInput}`);
      }
      // if (form.comment) {
      //   commentParts.push(`其他意見：${form.comment}`);
      // }
      const combinedComment = commentParts.join('；');

      let center = '';
      let centerAddress = '';
      if (form.serviceType === 'last') {
        center = selectedCenter.name;
        centerAddress = selectedCenter.address;
      } else if (form.serviceType === 'last-child') {
        center = twinFactory.name;
        centerAddress = twinFactory.address;
      } else if (form.serviceType === 'custom') {
        center = form.center;
        centerAddress = selectedCenter?.address || '';
      }
      setPendingOrder({
        ...form,
        center,
        centerAddress,
        comment: combinedComment,
      });
      setConfirmVisible(true);
    } else {
      setErrorDialog('請填寫所有必填欄位');
    }
  };

  useEffect(() => {
    if (notifyVisible && !form.notify && !tempNotify) {
      setTempNotify(notifyOptions[0]?.value || '');
    }
  }, [notifyVisible, form.notify, tempNotify]);

  const isDateDisabled =
    !form.serviceType ||
    (form.serviceType === 'custom' && !form.center) ||
    Object.keys(availableTime).length === 0;

  // 當服務據點選擇變動時，自動重置保養日期
  useEffect(() => {
    setForm((f) => ({ ...f, date: '' }));
    setTempDate('');
  }, [form.serviceType, form.center]);

  // [Submit Booking]
  const [submitting, setSubmitting] = useState(false);
  const handleSubmitBooking = async () => {
    try {
      setSubmitting(true);

      if (isEditMode) {
        const bookingToast = Toast.show({
          content: '更改預約中...',
          icon: 'loading',
          duration: 5000,
        });

        const repairs = prefillRecord?.repairs || '';
        const { rmFlag, fixFlag } = computeRmFixFlags();

        const payload = {
          car_no: carNo,
          dlr_code: selectedCenter.dlr_code,
          brnh_code: selectedCenter.brnh_code,
          brnh_name: selectedCenter.name,
          brnh_tel: selectedCenter.tel || '',
          date: pendingOrder.date,
          time: pendingOrder.time,
          contact_name: pendingOrder.name,
          contact_phone: pendingOrder.phone,
          contact_time: pendingOrder.contactTime,
          notify_type: pendingOrder.notify,
          remark: repairs,
          gv: pendingOrder.wait === 'yes',
          s_key: prefillRecord.sKey,
          rm_flag: rmFlag,
          fix_flag: fixFlag,
          opno_type: '',
        };

        const response = await modifyBooking(liff.getIDToken(), carNo, payload);
        bookingToast.close();
        setConfirmVisible(false);
        setCompleteDialog({
          visible: true,
          success: response.success,
          message: response.success ? '更改預約成功' : '更改預約失敗',
          detail: response.message,
          autoClose: response.success,
        });
        return;
      }

      const submitToast = Toast.show({
        content: '確認預約資格中...',
        icon: 'loading',
        duration: 10000,
      });

      const availableResponse = await fetchBookingAvailable(
        liff.getIDToken(),
        carNo
      );

      if (
        !availableResponse.success ||
        availableResponse.data?.book_23 !== 'Y'
      ) {
        submitToast.close();
        setConfirmVisible(false);
        Toast.show({
          content: '目前無法預約保養維修，您可能已有預約紀錄',
          icon: 'fail',
        });
        return;
      }

      submitToast.close();
      const bookingToast = Toast.show({
        content: '預約中...',
        icon: 'loading',
        duration: 5000,
      });

      console.log(`carNO`, carNo);
      console.log(`pendingOrder`, pendingOrder);
      const response = await submitMaintenanceBooking(
        liff.getIDToken(),
        carNo,
        pendingOrder,
        selectedCenter,
        serviceItems,
        otherRepairs,
        customText
      );
      bookingToast.close();
      setConfirmVisible(false);
      setCompleteDialog({
        visible: true,
        success: response.success,
        message: response.success ? '預約成功' : '預約失敗',
        detail: response.message,
        autoClose: response.success,
      });
    } catch (error) {
      console.error(error);
      Toast.show({
        content: error.message || '發送預約失敗',
        icon: 'fail',
        props: {
          style: { width: '500px' },
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  // [Complete Event]
  const [countdown, setCountdown] = useState(3);
  useEffect(() => {
    if (!liff || !completeDialog.autoClose) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        const newCountdown = prev <= 0 ? 0 : prev - 1;
        console.info('newCountdown', newCountdown);

        if (newCountdown <= 0) {
          liff.closeWindow();
          clearInterval(timer);
        }
        return newCountdown;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [completeDialog, liff]);

  if (!carNo) return <Fragment />;

  return (
    <div className="center-viewport">
      <div className="maintenance-app">
        {/* Toyota Logo */}
        <div className="toyota-header">
          <Link to="/">
            <img className="toyota-logo" src={toyotaLogo} alt="TOYOTA LOGO" />
          </Link>
        </div>
        {/* Main Content */}
        <div className="main-content">
          {/* Header */}
          <div className="content-header">
            <div className="header-row">
              <div className="header-text">
                <div className="vehicle-number">{form.plate}</div>
                <h1 className="page-title">
                  {isEditMode ? '預約保養維修更改' : '愛車預約管理'}
                </h1>
              </div>
              <div className="header-icons">
                <img src={carServiceBookingSvg} alt="預約保養icon" />
              </div>
            </div>

            {/* 3 個功能 Tab */}
            <div className="csb-tabs">
              <button type="button" className="csb-tab-btn csb-tab-btn--active">
                保養維修
              </button>
              <button
                type="button"
                className="csb-tab-btn"
                onClick={() => {
                  const params = new URLSearchParams();
                  if (carNo) params.set('carNo', carNo);
                  if (sKey) params.set('sKey', sKey);
                  const queryString = params.toString();
                  navigate(
                    queryString
                      ? `/hotai/toyota/car-service-booking/paint?${queryString}`
                      : '/hotai/toyota/car-service-booking/paint'
                  );
                }}>
                板噴估價
              </button>
              <button
                type="button"
                className="csb-tab-btn"
                onClick={() => {
                  const params = new URLSearchParams();
                  if (carNo) params.set('carNo', carNo);
                  if (sKey) params.set('sKey', sKey);
                  const queryString = params.toString();
                  navigate(
                    queryString
                      ? `/hotai/toyota/car-service-booking/record?${queryString}`
                      : '/hotai/toyota/car-service-booking/record'
                  );
                }}>
                預約紀錄
              </button>
            </div>
          </div>

          {/* 召修警告橫幅 */}
          <div className="csb-recall-warning">
            若您欲回廠進行
            <strong className="csb-recall-keyword">召修</strong>
            ，請撥服務據點電話預約。
          </div>
          {/* 表單區塊 */}
          <div className="car-service-container">
            {/* 服務項目 + 其他維修需求 */}
            {isEditMode ? (
              <div className="form-group">
                <label className="form-label">目前維修需求</label>
                <div className="csb-repair-textarea-wrap">
                  <textarea
                    className="csb-repair-textarea"
                    value={prefillRecord?.repairs || ''}
                    readOnly
                    disabled
                  />
                </div>
              </div>
            ) : (
              <>
                {/* 服務項目 */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> 服務項目
                    {errors.serviceSelection && (
                      <span className="form-error">
                        {errors.serviceSelection}
                      </span>
                    )}
                  </label>
                  <div className="csb-checkbox-item">
                    <CustomCheckbox
                      checked={serviceItems['定期保養']}
                      onChange={(v) =>
                        setServiceItems((s) => ({ ...s, 定期保養: v }))
                      }
                    />
                    <span className="csb-checkbox-label">
                      定期保養（滿月檢查）
                    </span>
                  </div>
                </div>

                {/* 其他維修需求 */}
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> 其他維修需求
                  </label>
                  <div className="csb-checkbox-row">
                    <div className="csb-checkbox-item">
                      <CustomCheckbox
                        checked={otherRepairs['導航圖資升級']}
                        onChange={(v) =>
                          setOtherRepairs((s) => ({ ...s, 導航圖資升級: v }))
                        }
                      />
                      <span className="csb-checkbox-label">導航圖資升級</span>
                    </div>
                    <div className="csb-checkbox-item">
                      <CustomCheckbox
                        checked={otherRepairs['異音、抖動']}
                        onChange={(v) =>
                          setOtherRepairs((s) => ({ ...s, '異音、抖動': v }))
                        }
                      />
                      <span className="csb-checkbox-label">異音、抖動</span>
                    </div>
                  </div>
                  <div className="csb-checkbox-item">
                    <CustomCheckbox
                      checked={otherRepairs['自行輸入']}
                      onChange={(v) =>
                        setOtherRepairs((s) => ({ ...s, 自行輸入: v }))
                      }
                    />
                    <span className="csb-checkbox-label">自行輸入</span>
                  </div>
                  <div className="csb-repair-textarea-wrap">
                    <textarea
                      className="csb-repair-textarea"
                      value={customText}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCustomText(val);
                        if (val && !otherRepairs['自行輸入']) {
                          setOtherRepairs((s) => ({ ...s, 自行輸入: true }));
                        }
                      }}
                      placeholder="●若您欲回廠進行召修，請撥服務據點電話預約。"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="form-group" data-field="plate">
              <label className="form-label">
                <span className="required">*</span> 車牌
                {errors.plate && (
                  <span className="form-error">{errors.plate}</span>
                )}
              </label>
              <div className="custom-input disabled">{form.plate}</div>
            </div>
            <div className="form-group" data-field="name">
              <label className="form-label">
                <span className="required">*</span> 姓名
                {errors.name && (
                  <span className="form-error">{errors.name}</span>
                )}
              </label>
              <Input
                className="custom-input"
                value={form.name}
                onChange={(v) => setForm((f) => ({ ...f, name: v }))}
              />
            </div>
            <div className="form-group" data-field="phone">
              <label className="form-label">
                <span className="required">*</span> 手機號碼
                {errors.phone && (
                  <span className="form-error">{errors.phone}</span>
                )}
              </label>
              <Input
                className="custom-input"
                value={form.phone}
                onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
              />
            </div>
            <div className="form-group" data-field="serviceType">
              <label className="form-label">
                <span className="required">*</span> 服務據點
                {errors.serviceType && (
                  <span className="form-error">{errors.serviceType}</span>
                )}
              </label>
              <Radio.Group
                value={form.serviceType}
                onChange={(v) => {
                  if (!isEditMode && !hasServiceSelection()) {
                    Toast.show({
                      content:
                        '請至少選擇「定期保養」或「其他維修需求」其中一項',
                      icon: 'fail',
                    });
                    return;
                  }
                  setForm((f) => ({ ...f, serviceType: v }));
                  if (v === 'last') {
                    if (lastBooking) {
                      setSelectedCenter(lastBooking);
                    } else if (lastAdviceFactory) {
                      setSelectedCenter(lastAdviceFactory);
                    }
                  } else if (v === 'last-child') {
                    if (twinFactory) {
                      setSelectedCenter(twinFactory);
                    }
                  } else {
                    setSelectedCenter(null);
                  }
                }}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* 變更定保預約 */}
                {lastBooking && (
                  <Radio
                    value="last"
                    className="location-radio-card"
                    style={{ display: 'flex', alignItems: 'flex-start' }}
                    disabled={
                      isEditMode && prefillRecord.canChangeFactory === 'N'
                    }>
                    <div className="location-location-radio-title-row">
                      <span className="location-radio-title">
                        {isServiceFactory(lastBooking.name)
                          ? '上次回廠服務據點'
                          : '上次回廠定保據點子保修站'}
                      </span>
                    </div>
                    <div className="location-desc">
                      {lastBooking.name}
                      <br />
                      {StringFormatter.toHalfWidthDigits(lastBooking.address)}
                    </div>
                  </Radio>
                )}
                {/* 新定保預約 */}
                {lastAdviceFactory && (
                  <Radio
                    value="last"
                    className="location-radio-card"
                    style={{ display: 'flex', alignItems: 'flex-start' }}
                    disabled={
                      isEditMode && prefillRecord.canChangeFactory === 'N'
                    }>
                    <div className="location-location-radio-title-row">
                      <span className="location-radio-title">
                        {isServiceFactory(lastAdviceFactory.name)
                          ? '上次回廠服務據點'
                          : '上次回廠定保據點子保修站'}
                      </span>
                    </div>
                    <div className="location-desc">
                      {lastAdviceFactory.name}
                      <br />
                      {StringFormatter.toHalfWidthDigits(
                        lastAdviceFactory.address
                      )}
                    </div>
                  </Radio>
                )}
                {twinFactory && (
                  <Radio
                    value="last-child"
                    className="location-radio-card"
                    style={{ display: 'flex', alignItems: 'flex-start' }}
                    disabled={
                      lastBooking?.modifiable === false ||
                      (isEditMode && prefillRecord.canChangeFactory === 'N')
                    }>
                    <div className="location-location-radio-title-row">
                      <span className="location-radio-title">
                        {isServiceFactory(twinFactory.name)
                          ? '上次回廠服務據點'
                          : '上次回廠定保據點子保修站'}
                      </span>
                    </div>
                    <div className="location-desc">
                      {twinFactory.name}
                      <br />
                      {StringFormatter.toHalfWidthDigits(twinFactory.address)}
                    </div>
                  </Radio>
                )}
                <Radio
                  value="custom"
                  className="location-radio-card"
                  style={{ display: 'flex', alignItems: 'flex-start' }}
                  disabled={
                    !serviceFactory ||
                    lastBooking?.modifiable === false ||
                    (isEditMode && prefillRecord.canChangeFactory === 'N')
                  }>
                  <div className="location-location-radio-title-row">
                    <span className="location-radio-title">
                      自行選擇服務據點
                      {fetchingFactory && (
                        <Spin size="small" style={{ paddingLeft: 4 }} />
                      )}
                    </span>
                  </div>
                  {form.serviceType === 'custom' && serviceFactory && (
                    <div className="custom-location-group">
                      <div className="form-group" data-field="city">
                        <label className="form-label">
                          縣市
                          {errors.city && (
                            <span className="form-error">{errors.city}</span>
                          )}
                        </label>
                        <div
                          className="custom-location-selector"
                          onClick={() => setCityVisible(true)}>
                          <span>{form.city || '請選擇'}</span>
                          <CaretDownOutlined style={{ fontSize: 16 }} />
                        </div>
                        <Popup
                          visible={cityVisible}
                          onMaskClick={() => setCityVisible(false)}
                          bodyStyle={{
                            borderTopLeftRadius: 20,
                            borderTopRightRadius: 20,
                            minHeight: 260,
                            padding: 0,
                          }}>
                          <div className="custom-popup-picker">
                            <div className="custom-popup-picker-title">
                              選擇縣市
                            </div>
                            <div className="custom-popup-picker-options">
                              <PickerView
                                columns={[
                                  Object.keys(serviceFactory).map((opt) => {
                                    return {
                                      label: opt,
                                      value: opt,
                                    };
                                  }),
                                ]}
                                onChange={(val) => setTempCity(val[0])}
                              />
                            </div>
                            <div className="custom-popup-picker-actions">
                              <button
                                type="button"
                                className="custom-popup-picker-btn cancel"
                                onClick={() => {
                                  setCityVisible(false);
                                  setTempCity('');
                                }}>
                                取消
                              </button>
                              <button
                                type="button"
                                className="custom-popup-picker-btn ok"
                                onClick={() => {
                                  setForm((f) => ({
                                    ...f,
                                    city: tempCity || form.city,
                                    district: '',
                                    center: '',
                                  }));
                                  setCityVisible(false);
                                  setTempCity('');
                                }}>
                                確定
                              </button>
                            </div>
                          </div>
                        </Popup>
                      </div>
                      <div className="form-group" data-field="district">
                        <label className="form-label">
                          區域
                          {errors.district && (
                            <span className="form-error">
                              {errors.district}
                            </span>
                          )}
                        </label>
                        <div
                          className={
                            'custom-location-selector' +
                            (!form.city ? ' disabled' : '')
                          }
                          onClick={() => form.city && setDistrictVisible(true)}>
                          <span>{form.district || '請選擇'}</span>
                          <CaretDownOutlined style={{ fontSize: 16 }} />
                        </div>
                        <Popup
                          visible={districtVisible}
                          onMaskClick={() => setDistrictVisible(false)}
                          bodyStyle={{
                            borderTopLeftRadius: 20,
                            borderTopRightRadius: 20,
                            minHeight: 260,
                            padding: 0,
                          }}>
                          <div className="custom-popup-picker">
                            <div className="custom-popup-picker-title">
                              選擇區域
                            </div>
                            <div className="custom-popup-picker-options">
                              <PickerView
                                columns={
                                  form.city
                                    ? [
                                        Object.keys(
                                          serviceFactory[form.city]
                                        ).map((opt) => {
                                          return {
                                            label: opt,
                                            value: opt,
                                          };
                                        }),
                                      ]
                                    : []
                                }
                                onChange={(val) => setTempDistrict(val[0])}
                              />
                            </div>
                            <div className="custom-popup-picker-actions">
                              <button
                                type="button"
                                className="custom-popup-picker-btn cancel"
                                onClick={() => {
                                  setDistrictVisible(false);
                                  setTempDistrict('');
                                }}>
                                取消
                              </button>
                              <button
                                type="button"
                                className="custom-popup-picker-btn ok"
                                onClick={() => {
                                  setForm((f) => ({
                                    ...f,
                                    district: tempDistrict || form.district,
                                    center: '',
                                  }));
                                  setDistrictVisible(false);
                                  setTempDistrict('');
                                }}
                                disabled={!form.city}>
                                確定
                              </button>
                            </div>
                          </div>
                        </Popup>
                      </div>
                      <div className="form-group" data-field="center">
                        <label className="form-label">
                          服務廠
                          {errors.center && (
                            <span className="form-error">{errors.center}</span>
                          )}
                        </label>
                        <div
                          className={
                            'custom-location-selector' +
                            (!form.district ? ' disabled' : '')
                          }
                          onClick={() =>
                            form.district && setCenterVisible(true)
                          }>
                          <span>{form.center || '請選擇'}</span>
                          <CaretDownOutlined style={{ fontSize: 16 }} />
                        </div>
                        <Popup
                          visible={centerVisible}
                          onMaskClick={() => setCenterVisible(false)}
                          bodyStyle={{
                            borderTopLeftRadius: 20,
                            borderTopRightRadius: 20,
                            minHeight: 260,
                            padding: 0,
                          }}>
                          <div className="custom-popup-picker">
                            <div className="custom-popup-picker-title">
                              選擇服務廠
                            </div>
                            <div className="custom-popup-picker-options">
                              <PickerView
                                columns={
                                  form.district
                                    ? [
                                        serviceFactory[form.city][
                                          form.district
                                        ].map((opt) => {
                                          return {
                                            label: opt.name,
                                            value: opt.name,
                                            ...opt,
                                          };
                                        }),
                                      ]
                                    : []
                                }
                                onChange={(val) => setTempCenter(val[0])}
                              />
                            </div>
                            <div className="custom-popup-picker-actions">
                              <button
                                type="button"
                                className="custom-popup-picker-btn cancel"
                                onClick={() => {
                                  setCenterVisible(false);
                                  setTempCenter('');
                                }}>
                                取消
                              </button>
                              <button
                                type="button"
                                className="custom-popup-picker-btn ok"
                                onClick={() => {
                                  setForm((f) => ({
                                    ...f,
                                    center: tempCenter || form.center,
                                  }));
                                  setSelectedCenter(
                                    serviceFactory[form.city][
                                      form.district
                                    ].find((c) => c.name === tempCenter)
                                  );
                                  setCenterVisible(false);
                                  setTempCenter('');
                                }}
                                disabled={!form.city || !form.district}>
                                確定
                              </button>
                            </div>
                          </div>
                        </Popup>
                        {form.center && selectedCenter && (
                          <div className="location-desc">{`${
                            selectedCenter.zip
                          } ${StringFormatter.toHalfWidthDigits(
                            selectedCenter.address
                          )} `}</div>
                        )}
                      </div>
                    </div>
                  )}
                </Radio>
              </Radio.Group>
            </div>
            {selectedCenter && (
              <div className="form-group" data-field="date">
                <label className="form-label">
                  <span className="required">*</span> 保養日期
                  {errors.date && (
                    <span className="form-error">{errors.date}</span>
                  )}
                </label>
                <div
                  className={
                    'custom-input date-picker-input' +
                    (isDateDisabled ? ' disabled' : '')
                  }
                  style={
                    isDateDisabled
                      ? {
                          cursor: 'not-allowed',
                          background: '#f2f2f2',
                          color: '#bbb',
                        }
                      : {}
                  }
                  onClick={() => {
                    if (!isDateDisabled) setCalendarVisible(true);
                  }}>
                  <span
                    style={{ flex: 1, color: form.date ? '#40474A' : '#bbb' }}>
                    {form.date
                      ? dayjs(form.date).format('YYYY-MM-DD')
                      : '請選擇'}
                  </span>
                  {fetchingTime ? (
                    <Spin size="small" style={{ paddingLeft: 4 }} />
                  ) : (
                    <CalendarOutline />
                  )}
                </div>
                <Popup
                  visible={calendarVisible}
                  onMaskClick={() => setCalendarVisible(false)}
                  bodyStyle={{
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,
                    minHeight: 320,
                    padding: 0,
                  }}>
                  <div className="custom-date-popup-picker">
                    <div className="custom-date-popup-picker-title">
                      選擇保養日期
                    </div>
                    <div className="custom-date-popup-picker-options">
                      <CalendarPickerView
                        value={
                          tempDate
                            ? dayjs(tempDate).toDate()
                            : form.date
                            ? dayjs(form.date).toDate()
                            : null
                        }
                        onChange={(val) =>
                          setTempDate(dayjs(val).format('YYYY-MM-DD'))
                        }
                        min={dayjs().add(1, 'day').toDate()}
                        max={dayjs().add(90, 'day').toDate()}
                        shouldDisableDate={(date) =>
                          !availableTime[dayjs(date).format('YYYY-MM-DD')]
                        }
                        renderHeader={null}
                      />
                    </div>
                    <div className="custom-date-popup-picker-actions">
                      <button
                        type="button"
                        className="custom-date-popup-picker-btn cancel"
                        onClick={() => {
                          setCalendarVisible(false);
                          setTempDate('');
                        }}>
                        取消
                      </button>
                      <button
                        type="button"
                        className="custom-date-popup-picker-btn ok"
                        onClick={() => {
                          setForm((f) => ({
                            ...f,
                            date: tempDate || form.date,
                          }));
                          setCalendarVisible(false);
                          setTempDate('');
                        }}
                        disabled={!tempDate && !form.date}>
                        確定
                      </button>
                    </div>
                  </div>
                </Popup>
              </div>
            )}
            {/* 保養時段欄位，未選日期時隱藏 */}
            {form.date && (
              <div className="form-group" data-field="time">
                <label className="form-label">
                  <span className="required">*</span> 保養時段
                  {errors.time && (
                    <span className="form-error">{errors.time}</span>
                  )}
                </label>
                <div className="time-select-grid">
                  {availableTime[form.date] &&
                  availableTime[form.date].length !== 0 ? (
                    availableTime[form.date].map((opt) => (
                      <div
                        key={opt}
                        className={`time-select-btn${
                          form.time === opt ? ' selected' : ''
                        }`}
                        onClick={() => setForm((f) => ({ ...f, time: opt }))}>
                        {opt}
                      </div>
                    ))
                  ) : (
                    <div className="no-time-available">本日無可預約時段</div>
                  )}
                </div>
              </div>
            )}
            <div className="form-group" data-field="wait">
              <label className="form-label">
                <span className="required">*</span> 是否在廠等候？
                {errors.wait && (
                  <span className="form-error">{errors.wait}</span>
                )}
              </label>
              <Radio.Group
                value={form.wait}
                onChange={(v) => setForm((f) => ({ ...f, wait: v }))}
                className="wait-radio-group">
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Radio
                    value="yes"
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginRight: '48px',
                    }}>
                    是
                  </Radio>
                  <Radio
                    value="no"
                    style={{ display: 'flex', alignItems: 'flex-start' }}>
                    否
                  </Radio>
                </div>
              </Radio.Group>
            </div>
            {/* 電話聯絡時段 */}
            {/* <div className="form-group" data-field="contactTime">
              <label className="form-label">
                <span className="required">*</span> 電話聯絡時段
                {errors.contactTime && (
                  <span className="form-error">{errors.contactTime}</span>
                )}
              </label>
              <div
                className="custom-selector"
                onClick={() => setContactTimeVisible(true)}>
                <span>
                  {form.contactTime
                    ? contactTimeOptions.find(
                      (opt) => opt.value === form.contactTime
                    )?.label
                    : '請選擇'}
                </span>
                <CaretDownOutlined style={{ fontSize: 16 }} />
              </div>
              <Popup
                visible={contactTimeVisible}
                onMaskClick={() => setContactTimeVisible(false)}
                bodyStyle={{
                  borderTopLeftRadius: 20,
                  borderTopRightRadius: 20,
                  minHeight: 260,
                  padding: 0,
                }}
              >
                <div className="custom-popup-picker">
                  <div className="custom-popup-picker-title">選擇聯絡時段</div>
                  <div className="custom-popup-picker-options">
                    <PickerView
                      columns={[contactTimeOptions]}
                      onChange={(val) => setTempContactTime(val[0])}
                    />
                  </div>
                  <div className="custom-popup-picker-actions">
                    <button
                      type="button"
                      className="custom-popup-picker-btn cancel"
                      onClick={() => {
                        setContactTimeVisible(false);
                        setTempContactTime('');
                      }}
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      className="custom-popup-picker-btn ok"
                      onClick={() => {
                        setForm((f) => ({
                          ...f,
                          contactTime: tempContactTime || form.contactTime,
                        }));
                        setContactTimeVisible(false);
                        setTempContactTime('');
                      }}
                    >
                      確定
                    </button>
                  </div>
                </div>
              </Popup>
            </div> */}

            {/* <div className="form-group" data-field="notify">
              <label className="form-label">
                <span className="required">*</span> 入廠提醒通知方式
                {errors.notify && (
                  <span className="form-error">{errors.notify}</span>
                )}
              </label>
              <div
                className="custom-selector"
                style={{ cursor: 'not-allowed', background: '#f2f2f2' }}
                // onClick={() => setNotifyVisible(true)}>
                onClick={() => console.log('No option selectable')}
              >
                <span>
                  {form.notify
                    ? notifyOptions.find((opt) => opt.value === form.notify)
                      ?.label
                    : '請選擇'}
                </span>
                <CaretDownOutlined style={{ fontSize: 16 }} />
              </div>
              <p style={{ color: '#bbb', fontSize: 12, marginTop: 1 }}>
                目前僅提供簡訊通知
              </p>
              <Popup
                visible={notifyVisible}
                onMaskClick={() => setNotifyVisible(false)}
                bodyStyle={{
                  borderTopLeftRadius: 20,
                  borderTopRightRadius: 20,
                  minHeight: 260,
                  padding: 0,
                }}
              >
                <div className="custom-popup-picker">
                  <div className="custom-popup-picker-title">
                    選擇入廠提醒通知方式
                  </div>
                  <div className="custom-popup-picker-options">
                    <PickerView
                      columns={[notifyOptions]}
                      onChange={(val) => setTempNotify(val[0])}
                    />
                  </div>
                  <div className="custom-popup-picker-actions">
                    <button
                      type="button"
                      className="custom-popup-picker-btn cancel"
                      onClick={() => {
                        setNotifyVisible(false);
                        setTempNotify('');
                      }}
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      className="custom-popup-picker-btn ok"
                      onClick={() => {
                        setForm((f) => ({
                          ...f,
                          notify: tempNotify || form.notify,
                        }));
                        setNotifyVisible(false);
                        setTempNotify('');
                      }}
                    >
                      確定
                    </button>
                  </div>
                </div>
              </Popup>
            </div> */}

            {/* <div className="form-group">
              <label className="form-label">其他意見</label>
              <TextArea
                className="custom-input comment-textbox"
                value={form.comment}
                onChange={(v) => setForm((f) => ({ ...f, comment: v }))}
                placeholder="限60字"
                maxLength={60}
                autoSize={{ minRows: 3, maxRows: 6 }}
                showCount
              />
            </div> */}
          </div>
        </div>
        {/* 底部固定送出按鈕 */}
        <div className="bottom-btn-bar">
          <button type="button" className="submit-btn" onClick={handleSubmit}>
            確定送出
          </button>
        </div>
        {/* 彈窗區塊 */}
        <Dialog
          visible={confirmVisible}
          showCloseButton={false}
          content={
            <div className="confirm-dialog">
              <div className="confirm-dialog-title">確認預約</div>
              <div className="confirm-dialog-row">
                <span className="confirm-dialog-label">保養日期</span>
                <span className="confirm-dialog-value">
                  {pendingOrder?.date
                    ? dayjs(pendingOrder.date).format('YYYY/M/D')
                    : ''}
                </span>
              </div>
              <div className="confirm-dialog-row">
                <span className="confirm-dialog-label">保養時間</span>
                <span className="confirm-dialog-value">
                  {pendingOrder?.time}
                </span>
              </div>
              <div className="confirm-dialog-row">
                <span className="confirm-dialog-label">服務據點</span>
                <span className="confirm-dialog-value">
                  {pendingOrder?.center}
                </span>
              </div>
              <div className="confirm-dialog-actions">
                <button
                  className="confirm-dialog-btn cancel"
                  onClick={() => setConfirmVisible(false)}
                  disabled={submitting}>
                  取消
                </button>
                <button
                  className="confirm-dialog-btn ok"
                  onClick={handleSubmitBooking}
                  disabled={submitting}>
                  確定
                </button>
              </div>
            </div>
          }
          actions={[]}
        />
        <Dialog
          visible={completeDialog.visible}
          showCloseButton={false}
          bodyStyle={{
            display: 'flex',
            height: completeDialog.autoClose ? '230px' : '320px',
            width: '230px',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            padding: 0,
          }}
          content={
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
                padding: '8px',
                margin: '0 auto',
                boxSizing: 'border-box',
              }}>
              {/* 上方內容區域 */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  width: '100%',
                  height: '100%',
                  margin: 'auto',
                }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '12px',
                  }}>
                  <img
                    src={completeDialog.success ? successSvg : failureSvg}
                    alt="booking status"
                    className={styles['status-icon']}
                    style={{ position: 'relative', zIndex: 1 }}
                  />
                </div>
                <div
                  className={styles['status-title']}
                  style={{
                    width: '100%',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    textAlign: 'center',
                  }}>
                  {completeDialog.message}
                </div>
                <div
                  className={styles['status-desc']}
                  style={{
                    width: '100%',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    textAlign: 'center',
                    marginTop: '8px',
                  }}>
                  {completeDialog.autoClose
                    ? `${countdown} 秒後將回到 LINE`
                    : completeDialog.detail}
                </div>
                {/* Special message for duplicate booking error */}
                {/重複預約/.test(completeDialog.detail) && (
                  <div
                    className={styles['status-desc-small']}
                    style={{
                      width: '100%',
                      wordBreak: 'break-word',
                      overflow: 'hidden',
                      textAlign: 'center',
                      marginTop: '8px',
                    }}>
                    *如需取消預約，請點選「車主服務」
                    <br />
                    頁面中「保修建議」按鈕進行調整。
                  </div>
                )}
              </div>

              {/* 底部按鈕區域 */}
              {!completeDialog.autoClose && (
                <div
                  style={{
                    width: '100%',
                    paddingTop: '16px', // 與上方內容保持間距
                  }}>
                  <button
                    style={{
                      backgroundColor: '#ef4444',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 16,
                      fontWeight: 500,
                      width: '100%',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                    }}
                    onClick={() => {
                      setCompleteDialog((prev) => ({
                        ...prev,
                        visible: false,
                      }));
                    }}>
                    好的
                  </button>
                </div>
              )}
            </div>
          }
          actions={[]}
        />
        <Dialog
          visible={!!errorDialog}
          showCloseButton={false}
          content={
            <div className="confirm-dialog error">
              <div className="confirm-dialog-title">請填寫所有必填欄位</div>
              <div className="confirm-dialog-actions">
                <button
                  className="confirm-dialog-btn ok"
                  onClick={() => {
                    setErrorDialog('');
                    // 用戶點擊"好的"後，自動跳轉到第一個未填寫的欄位
                    setTimeout(() => {
                      const firstErrorField = Object.keys(errors)[0];
                      const fieldElement = document.querySelector(
                        `[data-field="${firstErrorField}"]`
                      );
                      if (fieldElement) {
                        fieldElement.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center',
                        });
                        // 如果是輸入框，聚焦它
                        const inputElement =
                          fieldElement.querySelector('input');
                        if (inputElement) {
                          inputElement.focus();
                        }
                      }
                    }, 100);
                  }}>
                  好的
                </button>
              </div>
            </div>
          }
          actions={[]}
        />
      </div>
    </div>
  );
}
